// This file is auto-generated by @hey-api/openapi-ts

import type { Client, Options as Options2, TDataShape } from "./client";
import { client } from "./client.gen";
import type {
  AdminActivityActionsData,
  AdminActivityActionsResponses,
  AdminActivityAnalyticsData,
  AdminActivityAnalyticsErrors,
  AdminActivityAnalyticsResponses,
  AdminArchiveTrackData,
  AdminArchiveTrackErrors,
  AdminArchiveTrackResponses,
  AdminCreateTrackData,
  AdminCreateTrackErrors,
  AdminCreateTrackResponses,
  AdminDeleteUserData,
  AdminDeleteUserErrors,
  AdminDeleteUserResponses,
  AdminGetUserData,
  AdminGetUserErrors,
  AdminGetUserResponses,
  AdminInviteUserData,
  AdminInviteUserErrors,
  AdminInviteUserResponses,
  AdminListActivityData,
  AdminListActivityErrors,
  AdminListActivityResponses,
  AdminListUsersData,
  AdminListUsersResponses,
  AdminUpdateTrackData,
  AdminUpdateTrackErrors,
  AdminUpdateTrackResponses,
  AdminUpdateUserData,
  AdminUpdateUserErrors,
  AdminUpdateUserResponses,
  ChangePasswordData,
  ChangePasswordErrors,
  ChangePasswordResponses,
  CreatePersonalTokenData,
  CreatePersonalTokenResponses,
  GetActivityData,
  GetActivityResponses,
  GetDashboardData,
  GetDashboardResponses,
  GetHealthData,
  GetHealthResponses,
  GetMeData,
  GetMeDetailData,
  GetMeDetailResponses,
  GetMeResponses,
  GetSlackContextData,
  GetSlackContextErrors,
  GetSlackContextResponses,
  GetTrackData,
  GetTrackErrors,
  GetTrackResponses,
  ListTracksData,
  ListTracksResponses,
  ListTrackStatusesData,
  ListTrackStatusesErrors,
  ListTrackStatusesResponses,
  LoginData,
  LoginErrors,
  LoginResponses,
  LogoutData,
  LogoutResponses,
  PublishStatusData,
  PublishStatusErrors,
  PublishStatusResponses,
  SetProgramMilestonesData,
  SetProgramMilestonesErrors,
  SetProgramMilestonesResponses,
  SignupData,
  SignupErrors,
  SignupResponses,
  SlackDigestData,
  SlackDigestErrors,
  SlackDigestResponses,
  SlackInteractivityData,
  SlackInteractivityResponses,
  SlackNudgeData,
  SlackNudgeErrors,
  SlackNudgeResponses,
  SlackRefreshData,
  SlackRefreshErrors,
  SlackRefreshResponses,
  SteercoRefreshData,
  SteercoRefreshResponses,
  SteercoSlideData,
  SteercoSlideResponses,
  SubmitStatusData,
  SubmitStatusErrors,
  SubmitStatusResponses,
  UnpublishStatusData,
  UnpublishStatusErrors,
  UnpublishStatusResponses,
  UpdateProfileData,
  UpdateProfileErrors,
  UpdateProfileResponses,
  UpsertWeeklyStatusData,
  UpsertWeeklyStatusErrors,
  UpsertWeeklyStatusResponses,
} from "./types.gen";

export type Options<
  TData extends TDataShape = TDataShape,
  ThrowOnError extends boolean = boolean,
  TResponse = unknown,
> = Options2<TData, ThrowOnError, TResponse> & {
  /**
   * You can provide a client instance returned by `createClient()` instead of
   * individual options. This might be also useful if you want to implement a
   * custom client.
   */
  client?: Client;
  /**
   * You can pass arbitrary values through the `meta` object. This can be
   * used to access values that aren't defined as part of the SDK function.
   */
  meta?: Record<string, unknown>;
};

/**
 * Slack Refresh
 *
 * Read the program channel, draft status updates, and insert-only persist.
 *
 * Fetches ~26h of channel history, drafts suggestions via Anthropic, and for
 * each suggestion creates a `source='slack'` UNPUBLISHED draft ONLY when no
 * status row already exists for that track + current week (never overwrites —
 * existing rows are returned in `skipped`). When `post_digest=true`, also posts
 * the digest to the channel.
 *
 * Requires BOTH Slack and Anthropic to be configured, else 503.
 */
export const slackRefresh = <ThrowOnError extends boolean = false>(
  options?: Options<SlackRefreshData, ThrowOnError>,
) =>
  (options?.client ?? client).post<SlackRefreshResponses, SlackRefreshErrors, ThrowOnError>({
    url: "/integrations/slack/refresh",
    ...options,
  });

/**
 * Slack Digest
 *
 * Build the digest from current dashboard state and post it to the channel.
 *
 * When `dry_run=true`, the digest is built and its text is returned WITHOUT
 * posting anything to Slack (a preview). Requires Slack to be configured, else
 * 503.
 */
export const slackDigest = <ThrowOnError extends boolean = false>(
  options?: Options<SlackDigestData, ThrowOnError>,
) =>
  (options?.client ?? client).post<SlackDigestResponses, SlackDigestErrors, ThrowOnError>({
    url: "/integrations/slack/digest",
    ...options,
  });

/**
 * Slack Interactivity
 *
 * Handle a Slack interactive action (a digest or nudge button click).
 *
 * Slack POSTs ``application/x-www-form-urlencoded`` with a ``payload`` field
 * holding the interaction JSON. The RAW body is read first and its signature
 * verified (HMAC-SHA256 over ``v0:{ts}:{body}``) before anything is parsed;
 * an invalid/stale signature is rejected with 401.
 *
 * Two actions are handled, dispatched on ``action_id``:
 *
 * * ``slack_publish_week`` (the digest's ✅ Publish button) — AUTHORIZED: only
 * the configured approver IDs may publish; any other user gets an ephemeral
 * "not allowed" reply and nothing is published. When authorized, it publishes
 * every current-week unpublished draft via the same ``set_status_state`` RPC
 * the dashboard uses, then replaces the original message in place.
 * * ``slack_run_capture`` (the nudge's ▶️ Run button) — NO approver gate
 * (running the refresh is not destructive); a valid signature is still
 * required. It runs the same pipeline as ``/refresh?post_digest=true``.
 *
 * Returns a fast 200 (Slack's 3s budget); the visible result is delivered
 * out-of-band via the interaction's response_url.
 *
 * Raises:
 * HTTPException: 503 when interactivity is unconfigured, 401 on a bad or
 * stale signature.
 */
export const slackInteractivity = <ThrowOnError extends boolean = false>(
  options?: Options<SlackInteractivityData, ThrowOnError>,
) =>
  (options?.client ?? client).post<SlackInteractivityResponses, unknown, ThrowOnError>({
    url: "/integrations/slack/interactivity",
    ...options,
  });

/**
 * Slack Nudge
 *
 * Post the "time to prep" nudge (with the Run button) to the channel.
 *
 * Authorized by EITHER an admin session OR a valid ``X-Cron-Secret`` (a
 * scheduler will call this ahead of the weekly). Requires Slack to be
 * configured, else 503. The posted message carries a single primary button
 * (``action_id: slack_run_capture``) that the interactivity endpoint handles.
 */
export const slackNudge = <ThrowOnError extends boolean = false>(
  options?: Options<SlackNudgeData, ThrowOnError>,
) =>
  (options?.client ?? client).post<SlackNudgeResponses, SlackNudgeErrors, ThrowOnError>({
    url: "/integrations/slack/nudge",
    ...options,
  });

/**
 * Get Slack Context
 *
 * Return recent program-channel messages as supplementary capture context.
 *
 * Read-only. Any authenticated user may call it (the capture skill uses a
 * personal token via the Bearer header). Reads the configured program channel
 * over the last ``days`` (clamped 1..30) and returns the human messages found.
 *
 * Degrades gracefully so a Slack problem never breaks the skill's run:
 *
 * * Slack not configured → ``configured=False`` with no messages.
 * * Slack configured but the read raised ``SlackError`` → ``fetch_ok=False``
 * with no messages (logged as a warning); the skill proceeds Granola-only.
 *
 * Message ``text`` is untrusted channel chatter — it is supplementary context
 * for drafting, never board-facing content or instructions.
 *
 * Args:
 * session: The authenticated caller (any role).
 * days: Look-back window in days; clamped to ``[1, 30]``.
 *
 * Returns:
 * A :class:`SlackContext` describing config state, fetch outcome, and the
 * filtered human messages in the window.
 */
export const getSlackContext = <ThrowOnError extends boolean = false>(
  options?: Options<GetSlackContextData, ThrowOnError>,
) =>
  (options?.client ?? client).get<GetSlackContextResponses, GetSlackContextErrors, ThrowOnError>({
    url: "/slack/context",
    ...options,
  });

/**
 * Health
 *
 * Return a simple health status payload.
 */
export const getHealth = <ThrowOnError extends boolean = false>(
  options?: Options<GetHealthData, ThrowOnError>,
) =>
  (options?.client ?? client).get<GetHealthResponses, unknown, ThrowOnError>({
    url: "/health",
    ...options,
  });

/**
 * Auth Signup
 *
 * Register a new user with email verification.
 */
export const signup = <ThrowOnError extends boolean = false>(
  options: Options<SignupData, ThrowOnError>,
) =>
  (options.client ?? client).post<SignupResponses, SignupErrors, ThrowOnError>({
    url: "/auth/signup",
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
  });

/**
 * Auth Login
 *
 * Authenticate via Supabase, then set the signed session cookie.
 *
 * The Supabase access/refresh tokens never leave the backend — the browser
 * only ever sees the HMAC cookie this endpoint sets.
 */
export const login = <ThrowOnError extends boolean = false>(
  options: Options<LoginData, ThrowOnError>,
) =>
  (options.client ?? client).post<LoginResponses, LoginErrors, ThrowOnError>({
    url: "/auth/login",
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
  });

/**
 * Auth Logout
 *
 * Sign out — clear the session cookie.
 */
export const logout = <ThrowOnError extends boolean = false>(
  options?: Options<LogoutData, ThrowOnError>,
) =>
  (options?.client ?? client).post<LogoutResponses, unknown, ThrowOnError>({
    url: "/auth/logout",
    ...options,
  });

/**
 * Auth Create Personal Token
 *
 * Mint a long-lived personal access token for the current user.
 *
 * Any authenticated user mints their OWN token (the identity and role are
 * taken from the caller's session, never from the request body). The token
 * authenticates via an `Authorization: Bearer <token>` header, letting
 * non-interactive clients (e.g. the weekly-status-capture skill) call the API
 * without an interactive login every run.
 *
 * The token carries the caller's role as a claim, but admin actions still
 * re-verify the live role via `require_admin`, so the claim alone never grants
 * admin. It is long-lived (one year) — treat it like a password and store it
 * safely. Revocation is coarse: rotating `AUTH_SECRET` invalidates ALL tokens
 * at once; there is no per-user revocation yet.
 */
export const createPersonalToken = <ThrowOnError extends boolean = false>(
  options?: Options<CreatePersonalTokenData, ThrowOnError>,
) =>
  (options?.client ?? client).post<CreatePersonalTokenResponses, unknown, ThrowOnError>({
    url: "/auth/token",
    ...options,
  });

/**
 * Auth Me
 *
 * Get the current user's profile.
 */
export const getMe = <ThrowOnError extends boolean = false>(
  options?: Options<GetMeData, ThrowOnError>,
) =>
  (options?.client ?? client).get<GetMeResponses, unknown, ThrowOnError>({
    url: "/auth/me",
    ...options,
  });

/**
 * Auth Me Detail
 *
 * Get the current user's profile with timeline fields.
 */
export const getMeDetail = <ThrowOnError extends boolean = false>(
  options?: Options<GetMeDetailData, ThrowOnError>,
) =>
  (options?.client ?? client).get<GetMeDetailResponses, unknown, ThrowOnError>({
    url: "/auth/me/detail",
    ...options,
  });

/**
 * Auth Update Profile
 *
 * Update current user's name.
 */
export const updateProfile = <ThrowOnError extends boolean = false>(
  options: Options<UpdateProfileData, ThrowOnError>,
) =>
  (options.client ?? client).put<UpdateProfileResponses, UpdateProfileErrors, ThrowOnError>({
    url: "/auth/profile",
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
  });

/**
 * Auth Change Password
 *
 * Change the current user's password (verifies current password first).
 */
export const changePassword = <ThrowOnError extends boolean = false>(
  options: Options<ChangePasswordData, ThrowOnError>,
) =>
  (options.client ?? client).put<ChangePasswordResponses, ChangePasswordErrors, ThrowOnError>({
    url: "/auth/password",
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
  });

/**
 * Auth Activity
 *
 * Get the current user's activity log.
 */
export const getActivity = <ThrowOnError extends boolean = false>(
  options?: Options<GetActivityData, ThrowOnError>,
) =>
  (options?.client ?? client).get<GetActivityResponses, unknown, ThrowOnError>({
    url: "/auth/activity",
    ...options,
  });

/**
 * Admin List Users
 *
 * List all users (admin only).
 */
export const adminListUsers = <ThrowOnError extends boolean = false>(
  options?: Options<AdminListUsersData, ThrowOnError>,
) =>
  (options?.client ?? client).get<AdminListUsersResponses, unknown, ThrowOnError>({
    url: "/admin/users",
    ...options,
  });

/**
 * Admin Delete User
 *
 * Delete a user (admin only). Self-deletion blocked.
 */
export const adminDeleteUser = <ThrowOnError extends boolean = false>(
  options: Options<AdminDeleteUserData, ThrowOnError>,
) =>
  (options.client ?? client).delete<AdminDeleteUserResponses, AdminDeleteUserErrors, ThrowOnError>({
    url: "/admin/users/{user_id}",
    ...options,
  });

/**
 * Admin Get User
 *
 * Get a single user detail with activity logs (admin only).
 */
export const adminGetUser = <ThrowOnError extends boolean = false>(
  options: Options<AdminGetUserData, ThrowOnError>,
) =>
  (options.client ?? client).get<AdminGetUserResponses, AdminGetUserErrors, ThrowOnError>({
    url: "/admin/users/{user_id}",
    ...options,
  });

/**
 * Admin Update User
 *
 * Update a user's name or role (admin only). Self-demotion blocked.
 */
export const adminUpdateUser = <ThrowOnError extends boolean = false>(
  options: Options<AdminUpdateUserData, ThrowOnError>,
) =>
  (options.client ?? client).put<AdminUpdateUserResponses, AdminUpdateUserErrors, ThrowOnError>({
    url: "/admin/users/{user_id}",
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
  });

/**
 * Admin Invite User
 *
 * Invite a user by email (admin only).
 */
export const adminInviteUser = <ThrowOnError extends boolean = false>(
  options: Options<AdminInviteUserData, ThrowOnError>,
) =>
  (options.client ?? client).post<AdminInviteUserResponses, AdminInviteUserErrors, ThrowOnError>({
    url: "/admin/invite",
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
  });

/**
 * Admin List Activity
 *
 * List activity across all users (admin only).
 *
 * Search, filtering, sorting, and pagination are all applied server-side by
 * the list_recent_activity RPC — the client sends params and renders results.
 */
export const adminListActivity = <ThrowOnError extends boolean = false>(
  options?: Options<AdminListActivityData, ThrowOnError>,
) =>
  (options?.client ?? client).get<
    AdminListActivityResponses,
    AdminListActivityErrors,
    ThrowOnError
  >({ url: "/admin/activity", ...options });

/**
 * Admin Activity Analytics
 *
 * Aggregated activity analytics for the admin dashboard (admin only).
 */
export const adminActivityAnalytics = <ThrowOnError extends boolean = false>(
  options?: Options<AdminActivityAnalyticsData, ThrowOnError>,
) =>
  (options?.client ?? client).get<
    AdminActivityAnalyticsResponses,
    AdminActivityAnalyticsErrors,
    ThrowOnError
  >({ url: "/admin/activity/analytics", ...options });

/**
 * Admin Activity Actions
 *
 * Distinct activity actions across all time (admin only).
 *
 * Used to populate the activity filter dropdown so it matches the full,
 * unbounded table scope rather than the windowed analytics breakdown.
 */
export const adminActivityActions = <ThrowOnError extends boolean = false>(
  options?: Options<AdminActivityActionsData, ThrowOnError>,
) =>
  (options?.client ?? client).get<AdminActivityActionsResponses, unknown, ThrowOnError>({
    url: "/admin/activity/actions",
    ...options,
  });

/**
 * List Tracks
 *
 * List every track with a snapshot of its latest published status.
 */
export const listTracks = <ThrowOnError extends boolean = false>(
  options?: Options<ListTracksData, ThrowOnError>,
) =>
  (options?.client ?? client).get<ListTracksResponses, unknown, ThrowOnError>({
    url: "/tracks",
    ...options,
  });

/**
 * Get Track
 *
 * Get one track with its weekly statuses.
 *
 * Unpublished statuses are included only when the caller is an admin or the
 * track's owner; everyone else sees published statuses only.
 */
export const getTrack = <ThrowOnError extends boolean = false>(
  options: Options<GetTrackData, ThrowOnError>,
) =>
  (options.client ?? client).get<GetTrackResponses, GetTrackErrors, ThrowOnError>({
    url: "/tracks/{track_id}",
    ...options,
  });

/**
 * Admin Create Track
 *
 * Create a new track (admin only).
 */
export const adminCreateTrack = <ThrowOnError extends boolean = false>(
  options: Options<AdminCreateTrackData, ThrowOnError>,
) =>
  (options.client ?? client).post<AdminCreateTrackResponses, AdminCreateTrackErrors, ThrowOnError>({
    url: "/admin/tracks",
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
  });

/**
 * Admin Archive Track
 *
 * Archive a track — sets is_active=false (admin only).
 */
export const adminArchiveTrack = <ThrowOnError extends boolean = false>(
  options: Options<AdminArchiveTrackData, ThrowOnError>,
) =>
  (options.client ?? client).delete<
    AdminArchiveTrackResponses,
    AdminArchiveTrackErrors,
    ThrowOnError
  >({ url: "/admin/tracks/{track_id}", ...options });

/**
 * Admin Update Track
 *
 * Update a track's fields (admin only, partial update).
 */
export const adminUpdateTrack = <ThrowOnError extends boolean = false>(
  options: Options<AdminUpdateTrackData, ThrowOnError>,
) =>
  (options.client ?? client).put<AdminUpdateTrackResponses, AdminUpdateTrackErrors, ThrowOnError>({
    url: "/admin/tracks/{track_id}",
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
  });

/**
 * Set Program Milestones
 *
 * Bulk-replace the program-wide milestone set (admin only).
 *
 * Deletes all existing milestones and inserts the supplied set, returning the
 * new rows ordered by week_start then sort_order.
 */
export const setProgramMilestones = <ThrowOnError extends boolean = false>(
  options: Options<SetProgramMilestonesData, ThrowOnError>,
) =>
  (options.client ?? client).post<
    SetProgramMilestonesResponses,
    SetProgramMilestonesErrors,
    ThrowOnError
  >({
    url: "/admin/program-milestones",
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
  });

/**
 * List Track Statuses
 *
 * List a track's weekly statuses (newest week first).
 *
 * Unpublished statuses are included only for the track owner or an admin.
 */
export const listTrackStatuses = <ThrowOnError extends boolean = false>(
  options: Options<ListTrackStatusesData, ThrowOnError>,
) =>
  (options.client ?? client).get<ListTrackStatusesResponses, ListTrackStatusesErrors, ThrowOnError>(
    { url: "/tracks/{track_id}/statuses", ...options },
  );

/**
 * Upsert Weekly Status
 *
 * Create or update a weekly status draft for a track (owner or admin).
 *
 * This is the ingest endpoint for AI-generated drafts as well as manual edits.
 * A status that is already published cannot be modified (the RPC raises).
 *
 * FULL-REPLACE semantics: the text fields (summary/progress/blockers/
 * next_steps) are overwritten wholesale with the request body — any field the
 * caller omits is stored as NULL, not left at its previous value. Callers must
 * therefore send the COMPLETE status body every time; there is no partial /
 * patch update for these fields. (rag/percent_complete/source/source_refs are
 * coalesced, so omitting those keeps the existing value.) This is intentional:
 * both the full manual form and the AI full-draft ingest always submit the
 * entire body.
 */
export const upsertWeeklyStatus = <ThrowOnError extends boolean = false>(
  options: Options<UpsertWeeklyStatusData, ThrowOnError>,
) =>
  (options.client ?? client).post<
    UpsertWeeklyStatusResponses,
    UpsertWeeklyStatusErrors,
    ThrowOnError
  >({
    url: "/tracks/{track_id}/statuses",
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
  });

/**
 * Submit Status
 *
 * Submit a status for review (owner or admin) — moves it to in_review.
 *
 * Only a `draft` may be submitted. Submitting anything else (e.g. an already
 * `published` status) returns 409 — this is what prevents a non-admin from
 * using submit to unpublish a published status and then edit it.
 */
export const submitStatus = <ThrowOnError extends boolean = false>(
  options: Options<SubmitStatusData, ThrowOnError>,
) =>
  (options.client ?? client).post<SubmitStatusResponses, SubmitStatusErrors, ThrowOnError>({
    url: "/statuses/{status_id}/submit",
    ...options,
  });

/**
 * Publish Status
 *
 * Publish a status (admin only) — stamps published_at/published_by.
 *
 * Allowed only from `draft` or `in_review`; an already-`published` status
 * returns 409.
 */
export const publishStatus = <ThrowOnError extends boolean = false>(
  options: Options<PublishStatusData, ThrowOnError>,
) =>
  (options.client ?? client).post<PublishStatusResponses, PublishStatusErrors, ThrowOnError>({
    url: "/statuses/{status_id}/publish",
    ...options,
  });

/**
 * Unpublish Status
 *
 * Unpublish a status (admin only) — reverts to draft, clears publish stamps.
 *
 * Allowed only from `published`; anything else returns 409.
 */
export const unpublishStatus = <ThrowOnError extends boolean = false>(
  options: Options<UnpublishStatusData, ThrowOnError>,
) =>
  (options.client ?? client).post<UnpublishStatusResponses, UnpublishStatusErrors, ThrowOnError>({
    url: "/statuses/{status_id}/unpublish",
    ...options,
  });

/**
 * Get Dashboard
 *
 * Cross-track dashboard: every track with status, countdowns, and rollups.
 */
export const getDashboard = <ThrowOnError extends boolean = false>(
  options?: Options<GetDashboardData, ThrowOnError>,
) =>
  (options?.client ?? client).get<GetDashboardResponses, unknown, ThrowOnError>({
    url: "/dashboard",
    ...options,
  });

/**
 * Steerco Slide
 *
 * Download the exec Steerco snapshot as a BOI-styled PPTX slide (admin only).
 *
 * Reads live tracks via the same `list_tracks_with_status` path the dashboard
 * uses, renders a single 16:9 slide in-memory, and returns it as a file
 * download. This is a synchronous admin read, NOT the Slack cron path.
 */
export const steercoSlide = <ThrowOnError extends boolean = false>(
  options?: Options<SteercoSlideData, ThrowOnError>,
) =>
  (options?.client ?? client).get<SteercoSlideResponses, unknown, ThrowOnError>({
    url: "/steerco/slide.pptx",
    ...options,
  });

/**
 * Steerco Refresh
 *
 * Smart-sync each champion track's Steerco cells with its structured fields.
 *
 * SMART-SYNC: for every champion track (`kind != "program"`) this derives
 * candidate cells from `stage`, the use-case counts, `selected_use_case`, and
 * the latest published summary, then syncs them into the stored snapshot —
 * updating counts/comment that have moved and chips whose STATE changed, while
 * preserving curated chip labels the tabs can't infer (see `sync_steerco`).
 * Only tracks that actually changed are written back (via the service-role
 * client), and the response returns a human-readable diff per track so an admin
 * can glance and approve.
 */
export const steercoRefresh = <ThrowOnError extends boolean = false>(
  options?: Options<SteercoRefreshData, ThrowOnError>,
) =>
  (options?.client ?? client).post<SteercoRefreshResponses, unknown, ThrowOnError>({
    url: "/steerco/refresh",
    ...options,
  });
